import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { usePostHog } from 'posthog-js/react';
import { FcGoogle } from 'react-icons/fc';
import { LuClipboardList, LuExternalLink } from 'react-icons/lu';
import { Button, buttonVariants } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { PatientPageLayout } from '@/components/PatientPageLayout';
import patientSatisfactionSurveyImage from '@/assets/patient-satisfaction-survey-image.png';
import { StarRating } from '@/components/StarRating';
import {
  PatientSatisfactionAction,
  usePatientSatisfactionSurvey,
  useSubmitPatientSatisfactionSurvey,
} from '@/hooks/usePatientSatisfactionSurvey';
import { UnavailableQuestionnairePage } from '@/pages/onboarding/UnavailableQuestionnairePage';

const formatConsultationDate = (date: string) =>
  new Date(date).toLocaleDateString('fr-FR', {
    timeZone: 'Europe/Paris',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

const parseNote = (raw: string | null) => {
  const note = Number(raw);
  return Number.isInteger(note) && note >= 1 && note <= 5 ? note : null;
};

const ActionBlock = ({
  action,
  onClick,
}: {
  action: PatientSatisfactionAction;
  onClick: () => void;
}) => {
  const isForm = action.type === 'INTERNAL_FORM';
  return (
    <div className="flex w-full flex-col items-center gap-4 rounded-2xl border border-gray-100 bg-white p-5 text-center shadow-[0px_1.7px_7px_0px_rgba(8,15,54,0.05)]">
      <div className="flex size-11 items-center justify-center rounded-full bg-indigo-50">
        {isForm ? (
          <LuClipboardList className="size-5 text-indigo-700" />
        ) : (
          <FcGoogle className="size-6" />
        )}
      </div>
      <div className="flex flex-col gap-1">
        <p className="font-semibold">
          {isForm
            ? 'Votre médecin souhaite en savoir plus'
            : "Aidez d'autres patients"}
        </p>
        <p className="text-sm text-gray-600">
          {isForm
            ? 'Votre médecin vous propose de répondre à un formulaire complémentaire pour améliorer la prise en charge de ses patients.'
            : 'Partagez votre expérience sur Google pour aider les prochains patients à trouver le praticien qui leur convient.'}
        </p>
      </div>
      <a
        href={action.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className={buttonVariants({ variant: 'ctaPrimary', size: 'md' })}
      >
        {isForm ? 'Répondre au formulaire' : 'Laissez un avis sur Google'}
        <LuExternalLink />
      </a>
      {isForm && (
        <p className="text-xs text-gray-500">Formulaire géré par le cabinet</p>
      )}
    </div>
  );
};

export const PatientSatisfactionSurveyPage = () => {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const posthog = usePostHog();

  const {
    data: survey,
    isLoading,
    isError,
    refetch,
  } = usePatientSatisfactionSurvey(id);
  const { mutate: submit, isLoading: isSubmitting } =
    useSubmitPatientSatisfactionSurvey(id);

  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submittedAction, setSubmittedAction] =
    useState<PatientSatisfactionAction | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const openedRef = useRef(false);

  useEffect(() => {
    if (!survey || survey.status !== 'active' || openedRef.current) return;
    openedRef.current = true;

    const note = parseNote(searchParams.get('note'));
    setRating(note);
    posthog?.capture('patient_satisfaction_page_opened', {
      surveyId: id,
      note,
    });
  }, [survey, searchParams, id, posthog]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="size-10" aria-label="Chargement" />
      </div>
    );
  }

  if (isError || !survey) {
    return <UnavailableQuestionnairePage pastConsultation={false} />;
  }

  const alreadyAnswered = survey.status === 'alreadyAnswered';
  const action = submittedAction ?? survey.action;

  const trackActionClick = () => {
    if (!action) return;
    posthog?.capture('patient_satisfaction_action_clicked', {
      surveyId: id,
      actionType: action.type,
      rating: rating ?? survey.rating,
    });
  };

  // Terminal and post-submit states share one screen: a constant thank-you
  // line, plus the action block when there is one.
  if (hasSubmitted || alreadyAnswered) {
    return (
      <div className="flex h-full flex-col px-4 n2xs:px-6 nsm:px-0">
        <PatientPageLayout
          title={
            <div className="flex flex-col items-center gap-6">
              <img
                src={patientSatisfactionSurveyImage}
                alt=""
                className="w-full max-w-[342px] rounded-2xl"
              />
              <h1 className="text-2xl font-semibold leading-tight min-[23rem]:text-3.5xl nxs:text-center">
                {hasSubmitted
                  ? 'Merci pour votre retour !'
                  : 'Votre retour a déjà été envoyé'}
              </h1>
            </div>
          }
          footer={<></>}
        >
          <div className="flex flex-col items-center gap-6 nxs:text-center">
            {action ? (
              <ActionBlock action={action} onClick={trackActionClick} />
            ) : (
              <p className="font-medium">Vous pouvez fermer cette page</p>
            )}
          </div>
        </PatientPageLayout>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col px-4 n2xs:px-6 nsm:px-0">
      <PatientPageLayout
        title={
          <>
            <h1 className="text-2xl font-semibold leading-tight min-[23rem]:text-3.5xl nxs:text-center">
              {/* Insécable avant le « ? », comme le veut la typographie française —
                  sans elle le point d'interrogation se retrouve seul sur sa ligne
                  dès que le nom du praticien remplit la précédente. */}
              Comment s&apos;est passée votre consultation avec{' '}
              {survey.professionalName.withLowercase}&nbsp;?
            </h1>
            <p className="pt-4 text-gray-500 nxs:text-center">
              Consultation du {formatConsultationDate(survey.consultationDate)}
            </p>
          </>
        }
        footer={
          <div className="flex w-full flex-col items-center gap-2">
            <Button
              variant="ctaPrimary"
              size="lg"
              disabled={isSubmitting || rating === null}
              onClick={() => {
                if (rating === null) return;
                submit(
                  { rating, comment: comment.trim() || undefined },
                  {
                    onSuccess: (data) => {
                      setSubmittedAction(data.action);
                      setHasSubmitted(true);
                    },
                    onError: (error) => {
                      // Only a 409 means "already submitted elsewhere"; any
                      // other failure must not be dressed up as a thank-you.
                      if (error.response?.status === 409) {
                        setHasSubmitted(true);
                        refetch();
                        return;
                      }
                      enqueueSnackbar(
                        "Votre retour n'a pas pu être envoyé, veuillez réessayer.",
                        { variant: 'error', autoHideDuration: 5000 },
                      );
                    },
                  },
                );
              }}
            >
              Envoyer mon retour
            </Button>
          </div>
        }
      >
        <div className="flex flex-col items-center gap-6 nxs:text-center">
          {/* Stays clickable — the only way to fix a misclick among 5
              adjacent tap targets in the email. */}
          <StarRating value={rating} onChange={setRating} />

          <Textarea
            id="patient-satisfaction-comment"
            aria-label="Remarques (facultatif)"
            placeholder={'Avez-vous des remarques à ajouter ? (facultatif)'}
            value={comment}
            maxLength={5000}
            onChange={(event) => setComment(event.target.value)}
          />

          <p className="text-sm text-gray-500">
            Votre retour sera communiqué {survey.professionalName.withToPrefix}{' '}
            de façon anonyme. Votre nom ne lui sera pas communiqué, sauf si vous
            choisissez de l&apos;indiquer dans les remarques libres
          </p>
        </div>
      </PatientPageLayout>
    </div>
  );
};
